import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Box, Stack, Button } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PlaceIcon from '@mui/icons-material/Place';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MapIcon from '@mui/icons-material/Map';
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';

const MENU_ITEMS = [
  { key: 'buses', label: 'Buses', icon: <DirectionsBusIcon fontSize="small" /> },
  { key: 'routes', label: 'Routes', icon: <AltRouteIcon fontSize="small" /> },
  { key: 'bus-stops', label: 'Bus Stops', icon: <PlaceIcon fontSize="small" /> },
  { key: 'add-route', label: 'Assign Routes', icon: <AssignmentTurnedInIcon fontSize="small" /> },
  { key: 'map', label: 'Track Map', icon: <MapIcon fontSize="small" /> },
];

const TabShell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.25, 0),
}));

const ModuleTabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  position: 'relative',
  flexShrink: 0,
  minWidth: 142,
  paddingInline: theme.spacing(1.8),
  paddingBlock: theme.spacing(1),
  borderRadius: 0,
  textTransform: 'none',
  fontWeight: active ? 800 : 700,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    boxShadow: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(1.6),
    right: theme.spacing(1.6),
    bottom: 0,
    height: 3,
    borderRadius: 6,
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
    transform: active ? 'scaleX(1)' : 'scaleX(0.35)',
    opacity: active ? 1 : 0,
    transition: 'all 180ms ease',
    transformOrigin: 'center',
  },
}));

const Transportation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const normalizedPath = location.pathname.replace(/\/+$/, '');
    if (normalizedPath === '/transportation') {
      navigate('/transportation/buses', { replace: true });
    }
  }, [location.pathname, navigate]);

  const activeMenu = useMemo(() => {
    const current = MENU_ITEMS.find((item) =>
      location.pathname.includes(`/transportation/${item.key}`)
    );
    return current?.key || 'buses';
  }, [location.pathname]);

  return (
    <PageContainer>
      <AdminPageHero
        title="Transportation"
        subtitle="Manage buses, routes, stops and live tracking in one place."
  
      />

      <TabShell>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 6 },
          }}
        >
          {MENU_ITEMS.map((item) => (
            <ModuleTabButton
              key={item.key}
              active={activeMenu === item.key ? 1 : 0}
              startIcon={item.icon}
              onClick={() => navigate(`/transportation/${item.key}`)}
            >
              {item.label}
            </ModuleTabButton>
          ))}
        </Stack>
      </TabShell>

      <Box
        key={activeMenu}
        sx={{
          width: '100%',
          minHeight: 360,
          mt: 1.5,
          animation: 'transport-tab-fade 180ms ease-out',
          '@keyframes transport-tab-fade': {
            '0%': {
              opacity: 0,
              transform: 'translateY(8px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        <Outlet />
      </Box>
    </PageContainer>
  );
};

export default Transportation;
